
import throttle from 'lodash/throttle';
import {format_price, parse_price} from './Util';

const currency = window.metaCurrency || '€';

const getRanges = ( isNonLinear = true ) => {
	if ( Array.isArray( isNonLinear ) ) {
		return isNonLinear;
	}

	return isNonLinear ? [100, 250, 500] : [];
}

const normalize = ( val, [min, max], isNonLinear = true ) => {
	const ranges = getRanges( isNonLinear )
	let result = ( val - min ) / ( max - min );

	const range = Array.from( ranges ).filter( value => {
		return min < value / 2 && max > value * 1.5;
	} );

	if ( range.length > 0 ) {
		const rangestep = 1 / ( range.length + 1 );
		let current = min;

		for ( var i = 0; i <= range.length; i++ ) {
			let next = range[i];
			if ( ! next ) {
				next = max;
			}

			if ( val > current ) {
				result = ( val - current ) / ( next - current ) / ( range.length + 1 ) + ( rangestep * i );
			}
			current = next;
		}
	}

	return result;
}

const denormalize = ( val, [min, max], isNonLinear = true ) => {
	const ranges = getRanges( isNonLinear );
	let result = ( max - min ) * val + min;

	const range = Array.from( ranges ).filter( value => {
		return min < value / 2 && max > value * 1.5;
	} );

	if ( range.length > 0 ) {

		const rangestep = 1 / ( range.length + 1 );
		result = val * ( range[0] - min ) / rangestep + min;

		for ( var i = 1; i <= range.length; i++ ) {
			let next = range[i];
			if ( ! next ) {
				next = max;
			}

			if ( val > rangestep * i ) {
				result = ( val - rangestep * i ) * ( next - range[i - 1] ) / rangestep + range[i - 1];
			}
		}

	}

	return Math.round( result * 100 ) / 100;
}

class PriceSlider {

	constructor( parentEl ) {
		this.parentEl = parentEl;

		this.rangeEls = Array.from( this.parentEl.querySelectorAll( '.controls input[type=range]' ) );
		this.inputEls = Array.from( this.parentEl.querySelectorAll( '.slider-inputs input' ) );

		this.realMin = parseFloat( this.parentEl.getAttribute( 'data-real-min' ) );
		this.realMax = parseFloat( this.parentEl.getAttribute( 'data-real-max' ) );

		this.isNonLinear = this.parentEl.classList.contains( 'nonlinear' );

		// Add Event Listeners
		this.rangeEls.forEach( ( rangeEl ) => rangeEl.addEventListener( 'input', throttle( () => this.onSlide(), 50 ) ) );
		this.inputEls.forEach( ( inputEl ) => inputEl.addEventListener( 'input', () => this.onInput() ) );
		this.inputEls.forEach( ( inputEl ) => inputEl.addEventListener( 'blur', () => this.onBlur() ) );
	}

	onSlide() {

		const [rangeElFrom, rangeElTo] = this.rangeEls;

		// Get min and max values from the two range inputs.
		let minVal = Math.min( parseFloat( rangeElFrom.value ), parseFloat( rangeElTo.value ) );
		let maxVal = Math.max( parseFloat( rangeElFrom.value ), parseFloat( rangeElTo.value ) );

		// Denormalize the values if nonlinear.
		if ( this.isNonLinear ) {
			minVal = Math.floor( denormalize( minVal, [this.realMin, this.realMax], this.isNonLinear ) );
			maxVal = Math.ceil( denormalize( maxVal, [this.realMin, this.realMax], this.isNonLinear ) );
		}

		// Set values for hidden inputs and text inputs.
		this.setInputValues( minVal, maxVal );
	}

	onInput() {
		const [inputElFrom, inputElTo] = this.inputEls;

		// Get min and max values from the two text inputs.
		let minVal = Math.min( parse_price( inputElFrom.value, currency ), parse_price( inputElTo.value, currency ) );
		let maxVal = Math.max( parse_price( inputElFrom.value, currency ), parse_price( inputElTo.value, currency ) );

		// Set values for hidden inputs and ranges (exclude text).
		this.setInputValues( minVal, maxVal, '[type=hidden]' );
		this.setRangeValues( minVal, maxVal );
	}

	onBlur() {
		const [inputElFrom, inputElTo] = this.inputEls;

		// Get min and max values from the two text inputs.
		let minVal = Math.min( parse_price( inputElFrom.value, currency ), parse_price( inputElTo.value, currency ) );
		let maxVal = Math.max( parse_price( inputElFrom.value, currency ), parse_price( inputElTo.value, currency ) );

		minVal = Math.max( minVal, this.realMin );
		maxVal = Math.min( maxVal, this.realMax );

		// Set values for all inputs (include text).
		this.setInputValues( minVal, maxVal );
		this.setRangeValues( minVal, maxVal );
	}

	setInputValues( minVal, maxVal, filterSelector = false ) {
		const setInput = ( inputEl, newVal ) => {
			if ( filterSelector && ! inputEl.matches( filterSelector ) ) {
				return;
			}

			if ( inputEl.matches( '[data-price-format="true"]' ) ) {
				inputEl.value = format_price( newVal, currency );
			} else {
				inputEl.value = newVal;
			}
		}

		this.parentEl.querySelectorAll( '[data-price-from]' ).forEach( inputEl => setInput( inputEl, minVal ) );
		this.parentEl.querySelectorAll( '[data-price-to]' ).forEach( inputEl => setInput( inputEl, maxVal ) );
	}

	setRangeValues( minVal, maxVal ) {
		const [rangeElFrom, rangeElTo] = this.rangeEls;

		// Normalize the values if nonlinear, then set them to the rangeInput.
		if ( this.isNonLinear ) {
			minVal = normalize( minVal, [this.realMin, this.realMax], this.isNonLinear );
			maxVal = normalize( maxVal, [this.realMin, this.realMax], this.isNonLinear );
		}

		// Set rangeInput values.
		rangeElFrom.value = minVal;
		rangeElTo.value = maxVal;
	}
}


const initPriceSlider = function() {

	// Render all initial price sliders.
	document.querySelectorAll( '.js-price-slider' ).forEach( parentEl => {
		new PriceSlider( parentEl );
	} );

	// Render additional price sliders.
	window.addEventListener( 'render_chip.acl', ( evt ) => {

		const Chip = evt.detail.chip;
		const priceSliderEl = Chip.getElement().querySelector( '.js-price-slider' );
		if ( priceSliderEl ) {
			new PriceSlider( priceSliderEl );
		}
	} );
}

export {
	PriceSlider as default,
	initPriceSlider,
	normalize,
	denormalize
};
